import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import useImpersonation from '../../hooks/useImpersonation';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Pagination from '../../components/common/Pagination';
import AnnouncementCard from '../../components/announcements/AnnouncementCard';
import useEscapeKey from '../../hooks/useEscapeKey';
import useClubVocab from '../../hooks/useClubVocab';
import {
  getAnnouncements, createAnnouncement, deleteAnnouncement,
} from '../../services/announcementService';
import { MdCampaign, MdAdd, MdClose, MdSearch, MdExpandMore } from 'react-icons/md';

const TYPES = ['general', 'holiday', 'event', 'urgent'];

const SORT_OPTIONS = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc',  label: 'Oldest first' },
  { value: 'title:asc',      label: 'Title (A–Z)' },
  { value: 'type:asc',       label: 'Type (A–Z)' },
];

// ── Create Announcement Modal ─────────────────────────────────────────────
function PostModal({ onClose, onSuccess }) {
  const v = useClubVocab();
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: { type: 'general', targetAudience: 'all' },
  });
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const onSubmit = async (data) => {
    setSubmitting(true);
    setServerError('');
    try {
      await createAnnouncement(data);
      toast.success('Announcement posted');
      reset();
      onSuccess();
    } catch (err) {
      setServerError(err.response?.data?.message || 'Failed to post announcement');
    } finally {
      setSubmitting(false);
    }
  };

  useEscapeKey(onClose);

  const fieldCls = 'w-full bg-card border border-divider rounded-xl px-3 py-2.5 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-secondary transition-colors';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider">
          <h2 className="font-bold text-secondary text-base">Post announcement</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover transition-colors">
            <MdClose className="text-xl" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4 space-y-4">
          {serverError && (
            <div className="bg-red-100 text-red-700 text-sm rounded-xl px-4 py-3">{serverError}</div>
          )}

          <Input
            label="Title *"
            placeholder="e.g. Holiday Notice — Dashain"
            {...register('title', { required: 'Title is required' })}
            error={errors.title?.message}
          />

          <div>
            <label className="block text-sm font-medium text-secondary mb-1">Message *</label>
            <textarea
              {...register('message', { required: 'Message is required' })}
              rows={4}
              placeholder="Write your announcement here…"
              className={`${fieldCls} resize-none`}
            />
            {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Type</label>
              <select {...register('type')} className={fieldCls}>
                {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Audience</label>
              <select {...register('targetAudience')} className={fieldCls}>
                <option value="all">Everyone</option>
                <option value="students">{v.members}</option>
                <option value="trainers">Trainers</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-divider">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}>{submitting ? 'Posting…' : 'Post announcement'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Announcements() {
  document.title = 'Announcements — TrainAxis';
  const v = useClubVocab();
  const queryClient               = useQueryClient();
  const [typeFilter, setFilter]   = useState('');
  const [showModal, setShowModal] = useState(false);
  const { isReadOnly } = useImpersonation();

  // Pagination / sorting / search
  const [page, setPage]                 = useState(1);
  const [limit, setLimit]               = useState(10);
  const [sortBy, setSortBy]             = useState('createdAt');
  const [sortOrder, setSortOrder]       = useState('desc');
  const [search, setSearch]             = useState('');
  const [debouncedSearch, setDebounced] = useState('');
  const searchTimer = useRef(null);

  const { data, isPending } = useQuery({
    queryKey: ['announcements', typeFilter, page, limit, sortBy, sortOrder, debouncedSearch],
    queryFn:  () => {
      const params = { page, limit, sortBy, sortOrder };
      if (typeFilter)      params.type   = typeFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      return getAnnouncements(params).then(r => r.data);
    },
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteAnnouncement(id),
    onSuccess:  () => {
      toast.success('Announcement deleted');
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
    onError: () => toast.error('Failed to delete'),
  });

  const announcements = data?.announcements || data?.data || [];
  const total = data?.pagination?.total ?? announcements.length;
  const pages = data?.pagination?.totalPages ?? 1;

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebounced(val); setPage(1); }, 400);
  };

  // If a delete empties the current page, step back one page
  useEffect(() => {
    if (!isPending && total > 0 && announcements.length === 0 && page > 1) {
      setPage(p => Math.max(1, p - 1));
    }
  }, [isPending, total, announcements.length, page]);

  return (
    <div className="mx-auto w-full max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <p className="text-muted text-sm">{total} announcement{total !== 1 ? 's' : ''}</p>
        <Button
          onClick={() => setShowModal(true)}
          disabled={isReadOnly}
          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}>
          <MdAdd className="text-lg" /> Post announcement
        </Button>
      </div>

      {/* Type filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[['', 'All'], ...TYPES.map(t => [t, t.charAt(0).toUpperCase() + t.slice(1)])].map(([val, label]) => (
          <button
            key={val}
            onClick={() => { setFilter(val); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs border transition-all duration-200 ${
              typeFilter === val
                ? 'bg-secondary text-card font-semibold border-secondary'
                : 'bg-card text-muted font-medium border-divider hover:text-secondary'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search · sort · rows per page */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="relative flex-1 min-w-[180px]">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search title or message…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-full bg-card border border-divider text-secondary placeholder-subtle focus:outline-none focus:border-secondary focus:ring-2 focus:ring-primary/20 transition-colors"
          />
        </div>
        <div className="relative min-w-[150px]">
          <select
            value={`${sortBy}:${sortOrder}`}
            onChange={e => { const [b, o] = e.target.value.split(':'); setSortBy(b); setSortOrder(o); setPage(1); }}
            className="w-full appearance-none rounded-full pl-3 pr-9 py-2 text-sm bg-card border border-divider text-secondary focus:outline-none focus:border-secondary focus:ring-2 focus:ring-primary/20"
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
        </div>
        <div className="relative min-w-[110px]">
          <select
            value={limit}
            onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
            className="w-full appearance-none rounded-full pl-3 pr-9 py-2 text-sm bg-card border border-divider text-secondary focus:outline-none focus:border-secondary focus:ring-2 focus:ring-primary/20"
            title="Rows per page"
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
        </div>
      </div>

      {/* Cards grid */}
      {isPending ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-36 bg-nav-hover animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : !announcements.length ? (
        <Card>
          <div className="flex flex-col items-center py-14 text-center">
            <MdCampaign className="text-5xl text-subtle mb-3" />
            <p className="text-muted text-sm">No announcements yet</p>
            <p className="text-subtle text-xs mt-1">Post one to notify {v.members.toLowerCase()} and trainers</p>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {announcements.map(a => (
            <AnnouncementCard
              key={a._id}
              announcement={a}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="mt-4 bg-card border border-divider rounded-2xl">
          <Pagination page={page} pages={pages} total={total} limit={limit} onChange={(p) => setPage(p)} />
        </div>
      )}

      {showModal && (
        <PostModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            queryClient.invalidateQueries({ queryKey: ['announcements'] });
          }}
        />
      )}
    </div>
  );
}

export default Announcements;
